import type { ReactNode } from 'react';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { DisplaySizes } from '@newcore/ui/enums/typography-display-sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Display, Text } from '@newcore/ui/typography';
import newcoreAppIconLight from '@/assets/newcore-app-icon-light.svg';
import { useMobileInstallApp } from '@/features/onboarding/hooks/use-mobile-install-app';
import {
  getAppStoreBadgeUrl,
  getPlayStoreBadgeUrl,
} from '@/features/onboarding/utils/store-badges';
import { InstallStep } from './install-step';
import { StoreBadge } from './store-badge';

const PLAY_STORE_BADGE_ALT = 'Get it on Google Play';
const APP_STORE_BADGE_ALT = 'Download on the App Store';

interface MobileInstallAppProps {
  deepLinkUrl: string;
  title: string;
  children: ReactNode;
  notice?: string;
}

function storeName(showAppStore: boolean, showPlayStore: boolean) {
  if (showAppStore && showPlayStore) {
    return 'the App Store or Google Play';
  }

  return showAppStore ? 'the App Store' : 'Google Play';
}

export function MobileInstallApp({
  deepLinkUrl,
  title,
  notice,
  children,
}: MobileInstallAppProps) {
  const {
    playStoreUrl,
    appStoreUrl,
    showPlayStore,
    showAppStore,
    handleOpenApp,
  } = useMobileInstallApp({ deepLinkUrl });

  return (
    <div className='h-dvh overflow-y-auto bg-primary'>
      <div className='mx-auto flex min-h-full w-full max-w-md flex-col px-6 pb-6 pt-10 text-center'>
        <div className='flex flex-col items-center gap-5'>
          <div className='flex h-20 w-20 items-center justify-center rounded-[18px] bg-primary-solid'>
            <img
              src={newcoreAppIconLight}
              alt='NewCore'
              className='h-12 w-12'
            />
          </div>
          <Display
            as='h1'
            size={DisplaySizes.SM}
            weight={FontWeights.REGULAR}
            className='text-primary'
          >
            {title}
          </Display>
        </div>

        {notice && (
          <Text
            as='p'
            size={TextSizes.MD}
            className='mt-4 text-tertiary'
            data-testid='mobile-install-app-notice'
          >
            {notice}
          </Text>
        )}

        <ol
          className='mt-8 flex flex-col gap-[18px] rounded-[20px] bg-secondary p-5 text-left'
          data-testid='install-steps'
        >
          <InstallStep number={1}>
            Download NewCore from {storeName(showAppStore, showPlayStore)}
          </InstallStep>
          {children}
        </ol>

        <div className='mt-auto flex flex-col items-center pt-4'>
          <div
            className='flex flex-col items-center'
            data-testid='store-badges'
          >
            {showAppStore && (
              <StoreBadge
                href={appStoreUrl}
                src={getAppStoreBadgeUrl()}
                alt={APP_STORE_BADGE_ALT}
                testId='app-store-badge'
              />
            )}
            {showPlayStore && (
              <StoreBadge
                href={playStoreUrl}
                src={getPlayStoreBadgeUrl()}
                alt={PLAY_STORE_BADGE_ALT}
                testId='play-store-badge'
              />
            )}
          </div>
          <Text as='p' size={TextSizes.MD} className='py-3.5 text-tertiary'>
            Already installed?{' '}
            <button
              type='button'
              onClick={handleOpenApp}
              data-testid='open-app-button'
            >
              Open NewCore
            </button>
          </Text>
        </div>
      </div>
    </div>
  );
}
